// frontend/src/manager_notifications/notificationManager.js

import axios from 'axios';
import { 
  NOTIFICATION_TYPES, 
  PRIORITY_LEVELS, 
  DEFAULT_FILTERS,
  EVENT_COLORS,
  REQUEST_STATUS
} from './notificationTypes';
import { formatRelativeTime, getPriorityFromEvent } from './notificationUtils';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Configuration axios pour le backend (avec cookies)
const apiClient = axios.create({
  baseURL: API_BASE,
  withCredentials: true
});

class NotificationManager {
  constructor() {
    this.cache = {
      events: null,
      alerts: null,
      chatMessages: null,
      clientRequests: null,
      lastFetch: null
    };
    this.cacheDuration = 30000; // 30 secondes
    this.listeners = [];
  }

  /**
   * Abonnement aux changements de notifications
   */
  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notifie tous les listeners
   */
  notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  /**
   * Récupère tous les événements du planning
   */
  async fetchEvents() {
    try {
      // ✅ Utiliser apiClient au lieu de axios avec Authorization header
      const response = await apiClient.get('/schedule/events', {
        params: { limit: 100 }
      });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching events:', error);
      return [];
    }
  }

  /**
   * Récupère l'historique des alertes
   */
  async fetchAlerts() {
    try {
      // ✅ Utiliser apiClient
      const response = await apiClient.get('/alerts/history', {
        params: { limit: 50 }
      });
      return response.data.history || [];
    } catch (error) {
      console.error('Error fetching alerts:', error);
      return [];
    }
  }

  /**
   * Récupère les messages récents du chat (avec demandes RDV)
   */
  async fetchChatMessages(roomId = null) {
    try {
      // ✅ Utiliser apiClient
      const roomsResponse = await apiClient.get('/chat/rooms');
      
      const rooms = roomsResponse.data || [];
      if (rooms.length === 0) return [];
      
      const targetRoom = roomId || rooms[0]?.id;
      if (!targetRoom) return [];
      
      const messagesResponse = await apiClient.get(`/chat/messages/${targetRoom}`, {
        params: { limit: 50 }
      });
      
      return messagesResponse.data || [];
    } catch (error) {
      console.error('Error fetching chat messages:', error);
      return [];
    }
  }

  /**
   * Récupère les demandes clients (fuel, maintenance)
   */
  async fetchClientRequests() {
    try {
      // ✅ Utiliser apiClient
      const response = await apiClient.get('/staff/client-requests', {
        params: { limit: 50 }
      });
      return response.data.requests || [];
    } catch (error) {
      console.error('Error fetching client requests:', error);
      return [];
    }
  }

  /**
   * Récupère toutes les données fraîches
   */
  async refreshAll() {
    try {
      const [events, alerts, chatMessages, clientRequests] = await Promise.all([
        this.fetchEvents(),
        this.fetchAlerts(),
        this.fetchChatMessages(),
        this.fetchClientRequests()
      ]);
      
      this.cache = {
        events,
        alerts,
        chatMessages,
        clientRequests,
        lastFetch: Date.now()
      };
      
      this.notifyListeners();
      return this.cache;
    } catch (error) {
      console.error('Error refreshing all data:', error);
      return this.cache;
    }
  }

  /**
   * Convertit un événement planning en notification standardisée
   */
  eventToNotification(event) {
    const eventTypeKey = event.event_type?.toUpperCase() || 'APPOINTMENT';
    const eventType = NOTIFICATION_TYPES[eventTypeKey] || NOTIFICATION_TYPES.APPOINTMENT;
    const priority = getPriorityFromEvent(event);
    const startTime = new Date(event.start_time);
    const isToday = startTime.toDateString() === new Date().toDateString();
    
    let progress = 0;
    let status = 'pending';
    if (event.check_in) progress += 33;
    if (event.check_out) progress += 33;
    if (event.billing_status === 'paid') progress += 34;
    if (progress === 100) status = 'completed';
    else if (progress > 0) status = 'in_progress';
    
    return {
      id: `event_${event.id}`,
      type: event.event_type,
      typeConfig: eventType,
      title: event.title,
      message: event.description || `${eventType.label} programmé`,
      priority: priority,
      priorityConfig: PRIORITY_LEVELS[priority] || PRIORITY_LEVELS.normal,
      timestamp: event.start_time,
      timestampRelative: formatRelativeTime(event.start_time),
      isToday: isToday,
      source: 'schedule',
      sourceId: event.id,
      status: status,
      progress: progress,
      metadata: {
        staff: event.staff_first_name ? `${event.staff_first_name} ${event.staff_last_name}` : null,
        staffId: event.staff_id,
        aircraft: event.tail_number,
        client: event.client_name,
        hangar: event.hangar_name,
        startTime: event.start_time,
        endTime: event.end_time,
        checkIn: event.check_in,
        checkOut: event.check_out,
        billingStatus: event.billing_status,
        grading: event.grading,
        squawk: event.squawk,
        fuelAmount: event.fuel_amount,
        fuelReimbursed: event.fuel_reimbursed
      },
      actionUrl: `/schedule?event=${event.id}`,
      color: EVENT_COLORS[event.event_type] || 'blue',
      canComplete: true
    };
  }

  /**
   * Convertit une alerte en notification standardisée
   */
  alertToNotification(alert) {
    const priorityMap = {
      emergency: 'critical',
      warning: 'high',
      info: 'low',
      success: 'normal'
    };
    const priority = priorityMap[alert.priority] || 'normal';
    const alertTypeKey = `${alert.priority?.toUpperCase()}_ALERT` || 'INFO_ALERT';
    const alertType = NOTIFICATION_TYPES[alertTypeKey] || NOTIFICATION_TYPES.INFO_ALERT;
    
    return {
      id: `alert_${alert.id}`,
      type: alert.priority,
      typeConfig: alertType,
      title: alert.title,
      message: alert.message,
      priority: priority,
      priorityConfig: PRIORITY_LEVELS[priority] || PRIORITY_LEVELS.normal,
      timestamp: alert.sent_at,
      timestampRelative: formatRelativeTime(alert.sent_at),
      isToday: new Date(alert.sent_at).toDateString() === new Date().toDateString(),
      source: 'alert',
      sourceId: alert.id,
      metadata: {
        channelsUsed: alert.channels_used,
        recipientsCount: alert.recipients_count,
        success: alert.success
      },
      actionUrl: null,
      color: priority === 'critical' ? 'red' : priority === 'high' ? 'orange' : 'blue',
      canComplete: false
    };
  }

  /**
   * Convertit un message chat en notification standardisée
   */
  chatMessageToNotification(message) {
    const isAppointmentRequest = message.message_type === 'appointment_request';
    const type = isAppointmentRequest ? 'appointment_request' : 'chat';
    const typeKey = type.toUpperCase();
    const typeConfig = NOTIFICATION_TYPES[typeKey] || NOTIFICATION_TYPES.CHAT_MESSAGE;
    
    let title = `${message.first_name || 'User'} ${message.last_name || ''}`.trim();
    let content = message.message || '';
    
    if (isAppointmentRequest && message.metadata) {
      title = `📅 ${message.metadata.title || 'Demande de RDV'}`;
      content = message.metadata.description || message.message || '';
    }
    
    if (title.length > 50) title = title.substring(0, 47) + '...';
    if (content.length > 100) content = content.substring(0, 97) + '...';
    
    return {
      id: `chat_${message.id}`,
      type: message.message_type || 'text',
      typeConfig: typeConfig,
      title: title,
      message: content,
      priority: isAppointmentRequest ? 'high' : 'normal',
      priorityConfig: PRIORITY_LEVELS[isAppointmentRequest ? 'high' : 'normal'],
      timestamp: message.created_at,
      timestampRelative: formatRelativeTime(message.created_at),
      isToday: new Date(message.created_at).toDateString() === new Date().toDateString(),
      source: 'chat',
      sourceId: message.id,
      metadata: {
        senderId: message.sender_id,
        senderName: `${message.first_name || ''} ${message.last_name || ''}`.trim(),
        roomId: message.room_id,
        messageType: message.message_type,
        appointmentData: message.metadata
      },
      actionUrl: `/chat?message=${message.id}`,
      color: isAppointmentRequest ? 'orange' : 'green',
      canComplete: isAppointmentRequest
    };
  }

  /**
   * Convertit une demande client en notification standardisée
   */
  clientRequestToNotification(request) {
    const isFuel = request.request_type === 'fuel';
    const typeConfig = isFuel ? NOTIFICATION_TYPES.FUEL_REQUEST : NOTIFICATION_TYPES.MAINTENANCE_REQUEST;
    const statusConfig = REQUEST_STATUS[request.status] || REQUEST_STATUS.pending;
    
    // Déterminer la priorité
    let priority = 'normal';
    if (request.priority === 'urgent') priority = 'critical';
    else if (request.priority === 'high') priority = 'high';
    
    // Construire le titre
    let title = '';
    if (isFuel) {
      title = `Demande carburant - ${request.quantity || '?'} gallons`;
    } else {
      title = `Demande maintenance - ${request.tail_number || 'N/A'}`;
    }
    
    // Ajouter le nom du client si disponible
    const clientName = request.client_name || `${request.client_first_name || ''} ${request.client_last_name || ''}`.trim();
    if (clientName) {
      title = `${clientName} - ${title}`;
    }
    
    return {
      id: `request_${request.id}`,
      type: request.request_type,
      typeConfig: typeConfig,
      title: title,
      message: request.description || `Demande de ${isFuel ? 'carburant' : 'maintenance'}`,
      priority: priority,
      priorityConfig: PRIORITY_LEVELS[priority] || PRIORITY_LEVELS.normal,
      timestamp: request.created_at,
      timestampRelative: formatRelativeTime(request.created_at),
      isToday: new Date(request.created_at).toDateString() === new Date().toDateString(),
      source: 'request',
      sourceId: request.id,
      status: request.status,
      statusConfig: statusConfig,
      metadata: {
        clientId: request.client_id,
        clientName: clientName,
        clientEmail: request.client_email,
        aircraft: request.tail_number,
        quantity: request.quantity,
        scheduledDate: request.scheduled_date,
        priority: request.priority,
        description: request.description,
        approvedBy: request.approved_by_first_name ? `${request.approved_by_first_name} ${request.approved_by_last_name}` : null,
        approvedAt: request.approved_at,
        completedAt: request.completed_at
      },
      actionUrl: `/staff/requests/${request.id}`,
      color: isFuel ? 'orange' : 'red',
      canApprove: request.status === 'pending'
    };
  }

  /**
   * Récupère toutes les notifications agrégées
   */
  async getAllNotifications(options = {}) {
    const forceRefresh = options.forceRefresh || false;
    
    if (forceRefresh || !this.cache.lastFetch || (Date.now() - this.cache.lastFetch) > this.cacheDuration) {
      await this.refreshAll();
    }
    
    const notifications = [];
    
    // Ajouter les événements
    if (this.cache.events && Array.isArray(this.cache.events)) {
      this.cache.events.forEach(event => {
        try {
          notifications.push(this.eventToNotification(event));
        } catch (err) {
          console.error('Error converting event to notification:', err);
        }
      });
    }
    
    // Ajouter les alertes
    if (this.cache.alerts && Array.isArray(this.cache.alerts)) {
      this.cache.alerts.forEach(alert => {
        try {
          notifications.push(this.alertToNotification(alert));
        } catch (err) {
          console.error('Error converting alert to notification:', err);
        }
      });
    }
    
    // Ajouter les messages chat
    if (this.cache.chatMessages && Array.isArray(this.cache.chatMessages)) {
      this.cache.chatMessages.forEach(message => {
        try {
          notifications.push(this.chatMessageToNotification(message));
        } catch (err) {
          console.error('Error converting chat message to notification:', err);
        }
      });
    }
    
    // Ajouter les demandes clients
    if (this.cache.clientRequests && Array.isArray(this.cache.clientRequests)) {
      this.cache.clientRequests.forEach(request => {
        try {
          notifications.push(this.clientRequestToNotification(request));
        } catch (err) {
          console.error('Error converting client request to notification:', err);
        }
      });
    }
    
    // Trier par priorité puis par date
    notifications.sort((a, b) => {
      const priorityCompare = (PRIORITY_LEVELS[a.priority]?.weight || 2) - (PRIORITY_LEVELS[b.priority]?.weight || 2);
      if (priorityCompare !== 0) return priorityCompare;
      return new Date(b.timestamp) - new Date(a.timestamp);
    });
    
    return notifications;
  }

  /**
   * Récupère les notifications à venir (événements + demandes en attente)
   */
  async getUpcomingNotifications(limit = 10) {
    const all = await this.getAllNotifications();
    const now = new Date();
    
    // Inclure les événements du planning ET les demandes clients en attente
    return all
      .filter(n => 
        (n.source === 'schedule' && new Date(n.timestamp) >= now) ||
        (n.source === 'request' && n.status === 'pending')
      )
      .slice(0, limit);
  }

  /**
   * Récupère les notifications urgentes (alertes critiques + demandes RDV + demandes urgentes)
   */
  async getUrgentNotifications() {
    const all = await this.getAllNotifications();
    return all.filter(n => 
      (n.source === 'alert' && (n.priority === 'critical' || n.priority === 'high')) ||
      (n.source === 'schedule' && n.priority === 'high') ||
      (n.source === 'request' && n.priority === 'critical') ||
      (n.type === 'appointment_request' && n.metadata?.appointmentData?.urgency === 'immediate')
    );
  }

  /**
   * Récupère le nombre de notifications non lues
   */
  getUnreadCount() {
    try {
      const readIds = JSON.parse(localStorage.getItem('read_notifications') || '[]');
      return 0;
    } catch {
      return 0;
    }
  }

  /**
   * Marque une notification comme lue
   */
  markAsRead(notificationId) {
    try {
      const readIds = JSON.parse(localStorage.getItem('read_notifications') || '[]');
      if (!readIds.includes(notificationId)) {
        readIds.push(notificationId);
        localStorage.setItem('read_notifications', JSON.stringify(readIds));
        this.notifyListeners();
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  /**
   * Marque toutes les notifications comme lues
   */
  async markAllAsRead() {
    try {
      const all = await this.getAllNotifications();
      const readIds = all.map(n => n.id);
      localStorage.setItem('read_notifications', JSON.stringify(readIds));
      this.notifyListeners();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }

  /**
   * Approuve une demande client
   */
  async approveRequest(requestId) {
    try {
      // ✅ Utiliser apiClient
      await apiClient.put(`/staff/client-requests/${requestId}/approve`, {});
      await this.forceRefresh();
      return true;
    } catch (error) {
      console.error('Error approving request:', error);
      return false;
    }
  }

  /**
   * Refuse une demande client
   */
  async rejectRequest(requestId, reason = '') {
    try {
      // ✅ Utiliser apiClient
      await apiClient.put(`/staff/client-requests/${requestId}/reject`, { reason });
      await this.forceRefresh();
      return true;
    } catch (error) {
      console.error('Error rejecting request:', error);
      return false;
    }
  }

  /**
   * Marque une demande comme terminée
   */
  async completeRequest(requestId) {
    try {
      // ✅ Utiliser apiClient
      await apiClient.put(`/staff/client-requests/${requestId}/complete`, {});
      await this.forceRefresh();
      return true;
    } catch (error) {
      console.error('Error completing request:', error);
      return false;
    }
  }

  /**
   * Filtre les notifications par critères
   */
  async filterNotifications(filters = {}) {
    const all = await this.getAllNotifications();
    
    return all.filter(notification => {
      if (filters.categories && filters.categories.length > 0) {
        const category = notification.typeConfig?.category;
        if (!filters.categories.includes(category)) {
          return false;
        }
      }
      
      if (filters.minPriority) {
        const minWeight = PRIORITY_LEVELS[filters.minPriority]?.weight || 3;
        const notifWeight = PRIORITY_LEVELS[notification.priority]?.weight || 2;
        if (notifWeight > minWeight) return false;
      }
      
      if (filters.sources && filters.sources.length > 0) {
        if (!filters.sources.includes(notification.source)) {
          return false;
        }
      }
      
      if (filters.types && filters.types.length > 0) {
        if (!filters.types.includes(notification.type)) {
          return false;
        }
      }
      
      if (filters.status && notification.status !== filters.status) {
        return false;
      }
      
      if (filters.maxAgeDays) {
        const age = (Date.now() - new Date(notification.timestamp)) / (1000 * 60 * 60 * 24);
        if (age > filters.maxAgeDays) return false;
      }
      
      if (filters.excludeCompleted && notification.status === 'completed') {
        return false;
      }
      
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const titleMatch = notification.title?.toLowerCase().includes(searchLower);
        const messageMatch = notification.message?.toLowerCase().includes(searchLower);
        if (!titleMatch && !messageMatch) return false;
      }
      
      return true;
    });
  }

  /**
   * Statistiques des notifications
   */
  async getStats() {
    const all = await this.getAllNotifications();
    
    const now = new Date();
    const today = now.toDateString();
    
    return {
      total: all.length,
      byPriority: {
        critical: all.filter(n => n.priority === 'critical').length,
        high: all.filter(n => n.priority === 'high').length,
        normal: all.filter(n => n.priority === 'normal').length,
        low: all.filter(n => n.priority === 'low').length
      },
      bySource: {
        schedule: all.filter(n => n.source === 'schedule').length,
        alert: all.filter(n => n.source === 'alert').length,
        chat: all.filter(n => n.source === 'chat').length,
        request: all.filter(n => n.source === 'request').length
      },
      urgent: all.filter(n => n.priority === 'critical' || n.priority === 'high').length,
      today: all.filter(n => new Date(n.timestamp).toDateString() === today).length,
      pendingRequests: all.filter(n => n.source === 'request' && n.status === 'pending').length
    };
  }

  /**
   * Force le rafraîchissement du cache
   */
  async forceRefresh() {
    await this.refreshAll();
    return this.cache;
  }

  /**
   * Vide le cache
   */
  clearCache() {
    this.cache = {
      events: null,
      alerts: null,
      chatMessages: null,
      clientRequests: null,
      lastFetch: null
    };
    this.notifyListeners();
  }
}

export const notificationManager = new NotificationManager();